import { ref } from 'vue';
import { useToastStore, type ToastOptions } from '@common/stores/ToastStore';

export interface ToastCallbackOptions<Args extends unknown[] = unknown[]> extends ToastOptions {
	onError?: ((error: unknown, ...args: Args) => unknown) | null;
	onComplete?: ((...args: Args) => unknown) | null;
}

/**
 * Fonction d'action enveloppée : appelable normalement, et porteuse d'un
 * `loading` réactif que l'on relie directement au `:loading` d'un bouton.
 */
export type ToastCallbackHandler<Args extends unknown[], T> = ((
	...args: Args
) => Promise<T | undefined>) & {
	readonly loading: boolean;
};

export function useToast() {
	const store = useToastStore();

	/**
	 * Enveloppe une action asynchrone.
	 *
	 * Gère l'état de chargement et la notification d'échec sans que l'appelant ait
	 * à écrire un try/catch et un `ref(false)` à chaque bouton. Le compteur permet
	 * des appels concurrents : `loading` ne retombe qu'au dernier terminé.
	 */
	function callback<Args extends unknown[], T = void>(
		fn: (...args: Args) => T | Promise<T>,
		options: ToastCallbackOptions<Args> = {},
	): ToastCallbackHandler<Args, T> {
		const pending = ref(0);

		const handler = (async (...args: Args) => {
			pending.value++;
			try {
				return (await fn(...args)) as T | undefined;
			} catch (error) {
				console.error(error);
				store.error({ message: (error as Error)?.message, ...options });
				options.onError?.(error, ...args);
				return undefined;
			} finally {
				pending.value--;
				options.onComplete?.(...args);
			}
		}) as ToastCallbackHandler<Args, T>;

		Object.defineProperty(handler, 'loading', {
			get: () => pending.value > 0,
			enumerable: true,
		});

		return handler;
	}

	return {
		success: store.success,
		error: store.error,
		warning: store.warning,
		info: store.info,
		dismiss: store.dismiss,
		callback,
	};
}
